import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, Trash2 } from 'lucide-react';
import Modal from './Modal';
import ConfirmModal from './ConfirmModal';

const TemplateVersionsModal = ({ show, template, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (show && template) {
      fetchVersions();
      fetchUser();
    }
  }, [show, template]);

  const fetchUser = async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setUser(json.user || json.data);
      }
    } catch (e) {
      console.error('Auth error:', e);
    }
  };

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/templates/${template.id}/versions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVersions(data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const showToast = (msg, type = 'success') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDelete = (version) => {
    setConfirmAction({
      title: `Smazat verzi v${version.version}?`,
      message: `Opravdu chcete smazat verzi ${version.version} šablony "${template?.name}"? Tato akce je nevratná.`,
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const res = await fetch(`/api/templates/${template.id}/versions/${version.version}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          
          if (res.ok) {
            await fetchVersions();
            showToast('Verze byla úspěšně smazána');
          } else {
            const data = await res.json();
            showToast(data.error || 'Chyba při mazání verze', 'error');
          }
        } catch (err) {
          console.error(err);
          showToast('Chyba při mazání verze', 'error');
        }
      }
    });
  };

  const handleDownload = async (version) => {
    try {
      const res = await fetch(`/api/templates/${template.id}/download-version/${version.version}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = version.original_name || `${template.name}_v${version.version}.docx`;
        a.click();
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!show) return null;

  return (
    <>
    <Modal show={show} zIndex={9000}>
      <div style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 25px rgba(0,0,0,0.15)', maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#333' }}>
            Verze šablony: {template?.name}
          </h3>
          <button onClick={onClose} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} color="#999" />
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#666' }}>
            <Loader2 size={24} className="spin" style={{ marginRight: '8px' }} />
            Načítám verze...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {versions.length === 0 ? (
              <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>Žádné verze nenalezeny</p>
            ) : (
              versions.map((v) => (
                <div
                  key={v.version}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: v.is_current ? '#E8F5E9' : '#F9F9F9',
                    borderRadius: '10px',
                    border: v.is_current ? '2px solid #388E3C' : '1px solid #E0E0E0'
                  }}
                >
                  <div>
                    <p style={{ fontWeight: 600, color: '#333', margin: '0 0 4px' }}>
                      v{v.version} {v.original_name || template.original_name}
                      {v.is_current && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', backgroundColor: '#388E3C', color: '#FFF', padding: '2px 8px', borderRadius: '4px' }}>
                          Aktuální
                        </span>
                      )}
                      {v.file_exists === false && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', backgroundColor: '#FFF3E0', color: '#E65100', padding: '2px 8px', borderRadius: '4px' }}>
                          Soubor nenalezen
                        </span>
                      )}
                    </p>
                    <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>
                      {new Date(v.created_at).toLocaleString('cs-CZ')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => v.file_exists !== false && handleDownload(v)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        backgroundColor: '#E53935',
                        color: '#FFF',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500
                      }}
                    >
                      <Download size={14} /> Stáhnout
                    </button>
                    {v.version !== 1 && (user?.role === 'admin' || user?.role === 'manager') && (
                      <button
                        onClick={() => handleDelete(v)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          backgroundColor: '#FFEBEE',
                          color: '#E53935',
                          border: '1px solid #FFCDD2',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      >
                        <Trash2 size={14} /> Smazat
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
      <ConfirmModal
        show={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmText="Smazat"
        cancelText="Zrušit"
        danger={confirmAction?.danger}
        onConfirm={confirmAction?.onConfirm}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: toast.type === 'success' ? '#4CAF50' : '#E53935',
          color: '#FFF',
          padding: '12px 20px',
          borderRadius: '8px',
          zIndex: 9600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {toast.message}
        </div>
      )}
    </>
  );
};

export default TemplateVersionsModal;
